import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import Modal from "@/components/admin/Modal";
import { Field, TextInput, TextArea } from "@/components/admin/FormControls";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", description: "", slug: "" };

function slugify(text) {
    return (text || "")
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");

    const load = () => api.get("/admin/categories").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openCreate = () => { setForm(empty); setEditing(null); setError(""); setOpen(true); };
    const openEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); setError(""); setOpen(true); };
    const save = async () => {
        setError("");
        try {
            if (editing) await api.put(`/admin/categories/${editing}`, form);
            else await api.post("/admin/categories", form);
            setOpen(false); await load();
        } catch (e) { setError(formatApiError(e)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Excluir esta categoria?")) return;
        try { await api.delete(`/admin/categories/${id}`); await load(); }
        catch (e) { alert(formatApiError(e)); }
    };

    return (
        <AdminLayout
            eyebrow="Organização"
            title="Categorias"
            actions={<button onClick={openCreate} data-testid="category-create-btn" className="btn-primary"><Plus className="w-4 h-4" /> Nova categoria</button>}
        >
            <div data-testid="categories-list" className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <table className="w-full">
                    <thead className="bg-[#FAFAF7] text-left">
                        <tr>
                            <th className="overline text-[#5C5C5C] px-6 py-4">Nome</th>
                            <th className="overline text-[#5C5C5C] px-6 py-4">Slug</th>
                            <th className="overline text-[#5C5C5C] px-6 py-4 hidden md:table-cell">Descrição</th>
                            <th className="px-6 py-4 w-32 text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(c => (
                            <tr key={c.id} data-testid={`category-row-${c.id}`} className="border-t border-black/5">
                                <td className="px-6 py-4 font-display">{c.name}</td>
                                <td className="px-6 py-4 font-body text-sm text-[#5C5C5C]">{c.slug}</td>
                                <td className="px-6 py-4 font-body text-sm text-[#5C5C5C] hidden md:table-cell line-clamp-1">{c.description}</td>
                                <td className="px-6 py-4 text-right whitespace-nowrap">
                                    <button onClick={() => openEdit(c)} data-testid={`category-edit-${c.id}`} className="inline-flex items-center gap-1 text-sm text-[#1A1A1A] hover:text-black mr-3"><Pencil className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => remove(c.id)} data-testid={`category-delete-${c.id}`} className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={4} className="px-6 py-12 text-center font-body text-[#5C5C5C]">Nenhuma categoria.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar categoria" : "Nova categoria"} size="md">
                <div className="space-y-5">
                    <Field label="Nome"><TextInput testId="category-name" value={form.name} onChange={v => {
                        const prevAuto = slugify(form.name);
                        setForm({
                            ...form,
                            name: v,
                            slug: (!form.slug || form.slug === prevAuto) ? slugify(v) : form.slug,
                        });
                    }} /></Field>
                    <Field label="Slug" hint="Gerado automaticamente; pode editar"><TextInput testId="category-slug" value={form.slug} onChange={v => setForm({ ...form, slug: v })} /></Field>
                    <Field label="Descrição"><TextArea testId="category-desc" value={form.description} onChange={v => setForm({ ...form, description: v })} rows={3} /></Field>
                    {error && <div data-testid="category-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>}
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={() => setOpen(false)} className="btn-ghost">Cancelar</button>
                        <button onClick={save} data-testid="category-save" className="btn-primary">Salvar</button>
                    </div>
                </div>
            </Modal>
        </AdminLayout>
    );
}
